// Pure logic for the Concierge board — unit-tested; the React page stays thin.

// Move the dragged tile so it occupies the target tile's slot, preserving the
// order of the rest. Returns a new array; no-op on unknown ids or self-drop.
export function reorderTiles(tiles, draggedUserId, targetUserId) {
  if (draggedUserId === targetUserId) return tiles;
  const from = tiles.findIndex(t => t.user_id === draggedUserId);
  const to = tiles.findIndex(t => t.user_id === targetUserId);
  if (from === -1 || to === -1) return tiles;
  const next = tiles.slice();
  const [moved] = next.splice(from, 1);
  next.splice(to, 0, moved);
  return next;
}

export function orderedIds(tiles) {
  return tiles.map(t => t.user_id);
}

// Server timestamps are UTC without a tz suffix. "Never" when absent.
export function formatLastManaged(iso) {
  if (!iso) return 'Never';
  const d = new Date(/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso) ? iso : iso + 'Z');
  if (isNaN(d.getTime())) return 'Never';
  return `${d.getUTCMonth() + 1}/${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCFullYear()).slice(-2)}`;
}

// --- Filter & sort (view-only; never persisted) -------------------------

// Parse a server timestamp to epoch ms, or null. Datetime strings are
// UTC-naive (no tz) — append 'Z' so they read as UTC, mirroring
// formatLastManaged. Date-only strings (e.g. "2026-07-15") already parse as
// UTC midnight, so they must NOT get a 'Z'.
function _ms(iso) {
  if (!iso) return null;
  const hasTime = iso.includes('T');
  const needsZ = hasTime && !/[zZ]|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(needsZ ? iso + 'Z' : iso);
  return isNaN(d.getTime()) ? null : d.getTime();
}

function _msOr(iso, fallback) {
  const m = _ms(iso);
  return m == null ? fallback : m;
}

// Keep tiles matching ALL active constraints. Empty/absent constraint = no
// filter on that axis. Returns a new array.
export function filterTiles(tiles, { search = '', stale = false, expiring = false, overdue = false, prozpectsOnly = false } = {}) {
  const term = (search || '').trim().toLowerCase();
  return tiles.filter(t => {
    if (prozpectsOnly && !t.prozpects_managed) return false;
    if (stale && !t.is_stale) return false;
    if (expiring && !t.is_expiring) return false;
    if (overdue && !((t.todo_overdue || 0) > 0)) return false;
    if (term) {
      const name = (t.name || '').toLowerCase();
      const school = (t.school || '').toLowerCase();
      if (!name.includes(term) && !school.includes(term)) return false;
    }
    return true;
  });
}

const _COMPARATORS = {
  // never-managed first (-Infinity), then oldest managed first
  stale: (a, b) => _msOr(a.last_managed_at, -Infinity) - _msOr(b.last_managed_at, -Infinity),
  // most-recent managed first; never-managed last
  recent: (a, b) => _msOr(b.last_managed_at, -Infinity) - _msOr(a.last_managed_at, -Infinity),
  // soonest/past-due expiry first; no-expiry last (+Infinity)
  expiring: (a, b) => _msOr(a.expires_at, Infinity) - _msOr(b.expires_at, Infinity),
  // A–Z by name, case-insensitive
  name: (a, b) => (a.name || '').toLowerCase().localeCompare((b.name || '').toLowerCase()),
  // most overdue to-dos first, then most waiting; missing counts read as 0
  todos: (a, b) => ((b.todo_overdue || 0) - (a.todo_overdue || 0))
    || ((b.todo_waiting || 0) - (a.todo_waiting || 0)),
};

// Return a new array sorted by mode. 'manual' or an unknown mode returns the
// input array unchanged (identity — caller keeps the saved order). Ties keep
// original relative order (stable).
export function sortTiles(tiles, mode) {
  const cmp = _COMPARATORS[mode];
  if (!cmp) return tiles;
  return tiles
    .map((t, i) => [t, i])
    .sort((a, b) => cmp(a[0], b[0]) || (a[1] - b[1]))
    .map(pair => pair[0]);
}
